/**
 * Coin calculation utilities for quests
 *
 * Currency: All values are in SILVER (1 gold = 100 silver)
 */

type ChallengeRating =
  | 'minor'
  | 'easy'
  | 'normal'
  | 'hard'
  | 'involved';
type Priority = 'low' | 'normal' | 'high' | 'urgent';
export type RealmTier =
  | 'wood'
  | 'copper'
  | 'silver'
  | 'gold'
  | 'platinum';

/**
 * Base coin value for each challenge rating (in silver)
 * Scaled 100x from old gold values for finer granularity
 */
export function challengeCoinValue(
  challenge: ChallengeRating
): number {
  switch (challenge) {
    case 'minor':
      return 100;
    case 'easy':
      return 150;
    case 'normal':
      return 300;
    case 'hard':
      return 450;
    case 'involved':
      return 700;
    default:
      return 100;
  }
}

/**
 * Multiplier for each priority level
 */
export function priorityCoinMultiplier(
  priority: Priority
): number {
  switch (priority) {
    case 'low':
      return 0.6;
    case 'high':
      return 1.4;
    case 'urgent':
      return 1.6;
    default:
      return 1;
  }
}

/**
 * Tier multiplier for realm progression
 * Wood=1x, Copper=2x, Silver=3x, Gold=4x, Platinum=5x
 */
export function tierMultiplier(tier: RealmTier): number {
  switch (tier) {
    case 'wood':
      return 1;
    case 'copper':
      return 2;
    case 'silver':
      return 3;
    case 'gold':
      return 4;
    case 'platinum':
      return 5;
    default:
      return 1;
  }
}

/**
 * Flat bonus per step within a tier (in silver)
 */
export const STEP_BONUS = 40;

/**
 * Calculate realm reward multiplier including step bonus
 */
export function realmRewardMultiplier(
  tier: RealmTier,
  step: number
): { multiplier: number; flatBonus: number } {
  return {
    multiplier: tierMultiplier(tier),
    flatBonus: step * STEP_BONUS,
  };
}

/**
 * Calculate total coin value for a quest (in silver)
 * Formula: ceil(base * multiplier / taskCount) * taskCount
 * This ensures coins are evenly divisible per task
 */
export function calculateQuestCoins(
  challenge: string,
  priority: string,
  taskCount: number
): number {
  const base = challengeCoinValue(
    challenge as ChallengeRating
  );
  const multiplier = priorityCoinMultiplier(
    priority as Priority
  );
  const raw = base * multiplier;
  const perTask = Math.ceil(raw / taskCount);
  return perTask * taskCount;
}

/**
 * Calculate coins per task with realm tier bonus (in silver)
 */
export function coinsPerTaskWithTier(
  challenge: string,
  priority: string,
  taskCount: number,
  tier: RealmTier = 'wood',
  step: number = 0
): number {
  const basePerTask = coinsPerTask(
    challenge,
    priority,
    taskCount
  );
  const { multiplier, flatBonus } = realmRewardMultiplier(
    tier,
    step
  );
  return Math.ceil(basePerTask * multiplier + flatBonus);
}

/**
 * Calculate coins per task for partial completion (in silver)
 */
export function coinsPerTask(
  challenge: string,
  priority: string,
  taskCount: number
): number {
  const total = calculateQuestCoins(
    challenge,
    priority,
    taskCount
  );
  return Math.ceil(total / taskCount);
}

/**
 * Format silver amount as gold and silver display
 * e.g., 1234 -> { gold: 12, silver: 34 }
 */
export function formatCurrency(silver: number): {
  gold: number;
  silver: number;
} {
  return {
    gold: Math.floor(silver / 100),
    silver: silver % 100,
  };
}

/**
 * Format silver amount as a text string with gold/silver notation
 * e.g., 1234 -> "12g 34s"
 */
export function formatCurrencyText(silver: number): string {
  const { gold, silver: s } = formatCurrency(silver);
  return `${gold}g ${s}s`;
}

// ============ Realm Upgrade Utilities ============

const TIERS: RealmTier[] = [
  'wood',
  'copper',
  'silver',
  'gold',
  'platinum',
];

/**
 * Get the 1-indexed tier number (wood=1, copper=2, etc.)
 */
export function tierNumber(tier: RealmTier): number {
  return TIERS.indexOf(tier) + 1;
}

/**
 * Get the next tier after the given tier, or null if at max
 */
export function nextTier(
  tier: RealmTier
): RealmTier | null {
  const idx = TIERS.indexOf(tier);
  if (idx === -1 || idx >= TIERS.length - 1) return null;
  return TIERS[idx + 1];
}

/**
 * Calculate upgrade cost in silver
 * Formula: 5 × (step + 1) × 10^(tier - 1) gold → convert to silver
 */
export function calculateUpgradeCost(
  tier: RealmTier,
  step: number
): number {
  const tierNum = tierNumber(tier);
  const goldCost =
    5 * (step + 1) * Math.pow(10, tierNum - 1);
  return goldCost * 100; // Convert to silver
}

/**
 * Calculate the next tier/step after an upgrade
 */
export function nextTierStep(
  tier: RealmTier,
  step: number
): { tier: RealmTier; step: number } | null {
  if (step < 9) {
    return { tier, step: step + 1 };
  }
  const next = nextTier(tier);
  if (!next) return null; // Already at platinum 9
  return { tier: next, step: 0 };
}

/**
 * Check if a realm has reached the free tier cap (copper 9)
 */
export function isAtFreeCap(
  tier: RealmTier,
  step: number
): boolean {
  const tierIdx = TIERS.indexOf(tier);
  // Free cap is copper (index 1), step 9
  if (tierIdx > 1) return true; // Already past copper
  if (tierIdx === 1 && step >= 9) return true; // At copper 9
  return false;
}

/**
 * Check if a realm is at the absolute maximum (platinum 9)
 */
export function isAtMaxTier(
  tier: RealmTier,
  step: number
): boolean {
  return tier === 'platinum' && step >= 9;
}
